import { AlertTriangle, CheckCircle2, Clock, Package, ShieldCheck, Ticket } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { IDCard } from "@/features/users/IDCard";
import { useSlaRisk } from "@/features/ai/hooks";
import { useTickets } from "@/features/tickets/hooks";
import type { TicketListItem, TicketPriority, TicketStatus } from "@/features/tickets/api";
import { PRIORITY_LABELS, STATUS_LABELS } from "@/features/tickets/labels";
import { slaBadge } from "@/features/tickets/sla";
import { useOverview } from "./hooks";
import { BarList, DonutChart, MiniLine } from "./charts";

/** Sections a KPI card can drill into (a subset of the app's views). */
export type DashboardTarget =
  | "tickets"
  | "customers"
  | "assets"
  | "inventory"
  | "reports";

/** Navigate to a section; when going to Tickets, an optional preset pre-filters
 * the list (open / overdue / resolved). */
export type NavigateFn = (target: DashboardTarget, preset?: string) => void;

/**
 * The dashboard has two shapes:
 *  - Staff with `report:read` (admins, managers) see the company-wide overview
 *    powered by `/reports/overview`.
 *  - Everyone else (engineers) sees a personal dashboard built from their own
 *    assigned tickets — the reports overview is management-only, so calling it
 *    would 403 with "You do not have permission to perform this action."
 */
export default function DashboardPage({
  onNavigate,
}: {
  onNavigate?: NavigateFn | undefined;
}) {
  const { can } = useAuth();
  return can("report:read") ? (
    <ManagementDashboard onNavigate={onNavigate} />
  ) : (
    <PersonalDashboard onNavigate={onNavigate} />
  );
}

function ManagementDashboard({
  onNavigate,
}: {
  onNavigate?: NavigateFn | undefined;
}) {
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useOverview();

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading dashboard…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof ApiError ? error.message : "Could not load the dashboard."}
      </p>
    );
  }

  const k = data.kpis;
  const cards: {
    label: string;
    value: string | number;
    icon: typeof Ticket;
    tone: string;
    target?: DashboardTarget;
    preset?: string;
  }[] = [
    { label: "Open tickets", value: k.open_tickets, icon: Ticket, tone: "", target: "tickets", preset: "open" },
    { label: "Overdue", value: k.overdue_tickets, icon: AlertTriangle, tone: "text-destructive", target: "tickets", preset: "overdue" },
    { label: "SLA compliance", value: `${k.sla_compliance_pct}%`, icon: ShieldCheck, tone: "text-sla-ok", target: "reports" },
    { label: "Avg resolution", value: `${k.avg_resolution_hours}h`, icon: Clock, tone: "" },
    { label: "Resolved (30d)", value: k.resolved_in_period, icon: CheckCircle2, tone: "text-sla-ok", target: "tickets", preset: "resolved" },
    { label: "Low stock", value: k.low_stock_products, icon: Package, tone: k.low_stock_products ? "text-sla-warn" : "", target: "inventory" },
    { label: "Expiring AMC", value: k.expiring_amc, icon: ShieldCheck, tone: k.expiring_amc ? "text-sla-warn" : "", target: "assets" },
    { label: "Active customers", value: k.active_customers, icon: Ticket, tone: "", target: "customers" },
  ];

  const byStatus = Object.entries(data.by_status).map(([label, value]) => ({ label, value }));
  const byPriority = ["critical", "high", "medium", "low"]
    .filter((p) => p in data.by_priority)
    .map((label) => ({ label, value: data.by_priority[label] ?? 0 }));
  const bySite = Object.entries(data.by_site ?? {})
    .map(([label, value]) => ({ label, value }))
    .slice(0, 8);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Welcome, {user?.full_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Overview for the last 30 days.
        </p>
      </div>

      <IDCard />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {cards.map((c) => {
          const clickable = c.target != null && onNavigate != null;
          return (
            <button
              key={c.label}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onNavigate?.(c.target!, c.preset)}
              className={
                "rounded-lg border border-border bg-card p-4 text-left text-card-foreground transition-colors " +
                (clickable
                  ? "cursor-pointer hover:border-primary/50 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  : "cursor-default")
              }
              aria-label={clickable ? `${c.label} — open` : c.label}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{c.label}</span>
                <c.icon className={`h-4 w-4 ${c.tone || "text-muted-foreground"}`} aria-hidden="true" />
              </div>
              <div className={`mt-2 text-2xl font-semibold tabular-nums ${c.tone}`}>{c.value}</div>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
          <h2 className="mb-4 text-sm font-medium">Tickets by status</h2>
          <DonutChart data={byStatus} />
        </section>
        <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
          <h2 className="mb-4 text-sm font-medium">Tickets by priority</h2>
          <BarList data={byPriority} />
        </section>
      </div>

      {bySite.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
          <h2 className="mb-4 text-sm font-medium">Tickets by site</h2>
          <BarList data={bySite} />
        </section>
      )}

      <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
        <h2 className="mb-4 text-sm font-medium">Created vs resolved per day</h2>
        <MiniLine data={data.created_per_day} />
      </section>

      <SlaRiskPanel onNavigate={onNavigate} />
    </div>
  );
}

const OPEN_STATES: readonly TicketStatus[] = [
  "open",
  "assigned",
  "in_progress",
  "waiting_customer",
];

/**
 * An engineer's own dashboard, built entirely from `/tickets` — which the
 * server already scopes to the tickets assigned to them. No company-wide
 * reporting, so it needs only `ticket:read`.
 */
function PersonalDashboard({
  onNavigate,
}: {
  onNavigate?: NavigateFn | undefined;
}) {
  const { user } = useAuth();
  // Pull the largest page the API allows (100) and summarise it client-side.
  // An engineer's assigned queue is small; the KPI cards still use the server's
  // `total` so the headline counts stay accurate beyond the first 100.
  const { data, isLoading, isError, error } = useTickets({ page_size: 100 });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading dashboard…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof ApiError ? error.message : "Could not load the dashboard."}
      </p>
    );
  }

  const tickets = data.items;
  const isOpen = (t: TicketListItem) => OPEN_STATES.includes(t.status);
  const openTickets = tickets.filter(isOpen);
  const overdue = openTickets.filter((t) => t.sla.resolution_breached);
  const resolved = tickets.filter((t) => t.status === "resolved" || t.status === "closed");

  const cards: {
    label: string;
    value: number;
    icon: typeof Ticket;
    tone: string;
    preset?: string;
  }[] = [
    { label: "My open tickets", value: openTickets.length, icon: Ticket, tone: "", preset: "open" },
    { label: "Overdue", value: overdue.length, icon: AlertTriangle, tone: overdue.length ? "text-destructive" : "", preset: "overdue" },
    { label: "Resolved", value: resolved.length, icon: CheckCircle2, tone: "text-sla-ok", preset: "resolved" },
    { label: "Assigned to me", value: data.total, icon: ShieldCheck, tone: "" },
  ];

  const statusCounts = new Map<TicketStatus, number>();
  for (const t of tickets) statusCounts.set(t.status, (statusCounts.get(t.status) ?? 0) + 1);
  const byStatus = [...statusCounts.entries()].map(([s, value]) => ({
    label: STATUS_LABELS[s],
    value,
  }));

  const byPriority = (["critical", "high", "medium", "low"] as TicketPriority[])
    .map((p) => ({ label: PRIORITY_LABELS[p], value: tickets.filter((t) => t.priority === p).length }))
    .filter((r) => r.value > 0);

  // Tickets I own that are close to (or past) their resolution deadline.
  const atRisk = openTickets
    .filter(
      (t) => t.sla.resolution_state === "warning" || t.sla.resolution_state === "breached",
    )
    .sort((a, b) => (a.sla.resolution_due_at ?? "").localeCompare(b.sla.resolution_due_at ?? ""))
    .slice(0, 8);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Welcome, {user?.full_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your assigned work.</p>
      </div>

      <IDCard />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => {
          const clickable = onNavigate != null;
          return (
            <button
              key={c.label}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onNavigate?.("tickets", c.preset)}
              className={
                "rounded-lg border border-border bg-card p-4 text-left text-card-foreground transition-colors " +
                (clickable
                  ? "cursor-pointer hover:border-primary/50 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  : "cursor-default")
              }
              aria-label={clickable ? `${c.label} — open tickets` : c.label}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{c.label}</span>
                <c.icon className={`h-4 w-4 ${c.tone || "text-muted-foreground"}`} aria-hidden="true" />
              </div>
              <div className={`mt-2 text-2xl font-semibold tabular-nums ${c.tone}`}>{c.value}</div>
            </button>
          );
        })}
      </div>

      {tickets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          You have no tickets assigned to you right now. New assignments will appear here.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
            <h2 className="mb-4 text-sm font-medium">My tickets by status</h2>
            <DonutChart data={byStatus} />
          </section>
          <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
            <h2 className="mb-4 text-sm font-medium">My tickets by priority</h2>
            <BarList data={byPriority} />
          </section>
        </div>
      )}

      {atRisk.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="h-4 w-4 text-sla-warn" aria-hidden="true" />
            My tickets at risk of SLA breach
          </h2>
          <div className="space-y-1.5">
            {atRisk.map((t) => {
              const badge = slaBadge(t.sla.resolution_state, t.sla.resolution_due_at);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onNavigate?.("tickets", "overdue")}
                  className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-1.5 text-left text-sm hover:bg-accent"
                >
                  <span className="truncate font-medium">{t.title}</span>
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    {t.ticket_number} · {t.customer_name}
                  </span>
                  <span className="ml-auto shrink-0">
                    <Badge tone={badge.tone}>{badge.label}</Badge>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function SlaRiskPanel({ onNavigate }: { onNavigate?: NavigateFn | undefined }) {
  const { data: risks } = useSlaRisk();
  if (!risks || risks.length === 0) return null;
  return (
    <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 text-sla-warn" aria-hidden="true" />
        At risk of SLA breach
      </h2>
      <div className="space-y-1.5">
        {risks.slice(0, 8).map((r) => {
          const tone = r.risk >= 0.9 ? "danger" : "warning";
          const left =
            r.minutes_left < 0
              ? "overdue"
              : r.minutes_left < 60
                ? `${r.minutes_left}m left`
                : `${Math.round(r.minutes_left / 60)}h left`;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onNavigate?.("tickets", "overdue")}
              className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-1.5 text-left text-sm hover:bg-accent"
            >
              <span className="truncate font-medium">{r.title}</span>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {r.ticket_number} · {r.customer_name ?? ""}
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-2">
                <span className="text-xs text-muted-foreground">{r.clock}</span>
                <Badge tone={tone}>{left}</Badge>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
